'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'

import { useCreditsShelfContext } from './context.js'
import type { CreditsShelfLayoutMode } from './types.js'

const PrevIcon = ({ disabled }: { disabled: boolean }) => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-info-primary laptop:block lg-desktop:size-[56px] hidden size-[40px]"
  >
    <path
      d="M0 28C0 43.464 12.536 56 28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28Z"
      fill="currentColor"
      fillOpacity={disabled ? '0.2' : '0.6'}
    />
    <path d="M31 22L25 28L31 34" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const NextIcon = ({ disabled }: { disabled: boolean }) => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-info-primary laptop:block lg-desktop:size-[56px] hidden size-[40px]"
  >
    <path
      d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28Z"
      fill="currentColor"
      fillOpacity={disabled ? '0.2' : '0.6'}
    />
    <path d="M25 22L31 28L25 34" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * 产品列表组件 Props
 */
export interface CreditsShelfProductsListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 布局模式：网格布局或横向滑动 */
  layoutMode?: CreditsShelfLayoutMode
  /** 子组件（ProductCard 或自定义卡片） */
  children: React.ReactNode
}

/**
 * CreditsShelf 产品列表组件
 *
 * @description 支持网格布局和横向滑动两种布局模式
 */
const CreditsShelfProductsList = React.forwardRef<HTMLDivElement, CreditsShelfProductsListProps>(
  ({ layoutMode = 'flex', className, children, ...props }, ref) => {
    const context = useCreditsShelfContext()
    const copy = context?.copy

    // 生成唯一 ID，移除特殊字符以确保可用于 CSS 选择器
    const id = React.useId().replace(/:/g, '')
    // 预定义导航按钮选择器，避免多次拼接
    const nextButtonClass = `swiper-button-next-custom-${id}`
    const prevButtonClass = `swiper-button-prev-custom-${id}`

    // Swiper 导航状态
    const [swiperState, setSwiperState] = React.useState({
      isBeginning: true,
      isEnd: false,
    })

    // Swiper 实例引用
    const swiperRef = React.useRef<any>(null)

    // 计算并设置所有 slide 为最高高度
    const equalizeSlideHeights = React.useCallback(() => {
      const swiper = swiperRef.current
      if (!swiper?.slides || swiper.slides.length === 0) return

      // 先重置所有 slide 高度以获取真实内容高度
      swiper.slides.forEach((slide: HTMLElement) => {
        slide.style.height = 'auto'
      })

      // 计算最大高度
      // @ts-ignore
      const heights = Array.from(swiper.slides).map((slide: HTMLElement) => slide.scrollHeight)
      const maxHeight = Math.max(...heights)

      // 设置所有 slide 为最大高度
      if (maxHeight > 0) {
        swiper.slides.forEach((slide: HTMLElement) => {
          slide.style.height = `${maxHeight}px`
        })
      }
    }, [])

    // 监听窗口 resize 重新计算高度
    React.useEffect(() => {
      const handleResize = () => {
        // 使用 requestAnimationFrame 确保 DOM 更新完成
        requestAnimationFrame(equalizeSlideHeights)
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [equalizeSlideHeights])

    // 将 children 转换为数组
    const childrenArray = React.Children.toArray(children)

    if (childrenArray.length === 0) {
      return null
    }

    return (
      <div ref={ref} className={className} {...props}>
        {layoutMode === 'flex' ? (
          /* 网格布局 */
          <div className="lg-desktop:gap-6 tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 grid grid-cols-1 justify-start gap-4">
            {childrenArray.map((child, index) => (
              <div key={index} className="shrink-0">
                {child}
              </div>
            ))}
          </div>
        ) : (
          /* Swiper 横向滑动布局 */
          <div className="group relative">
            <Swiper
              modules={[Navigation]}
              spaceBetween={16}
              slidesPerView="auto"
              navigation={{
                nextEl: `.${nextButtonClass}`,
                prevEl: `.${prevButtonClass}`,
              }}
              onSwiper={swiper => {
                swiperRef.current = swiper
                setSwiperState({
                  isBeginning: swiper.isBeginning,
                  isEnd: swiper.isEnd,
                })
                // 延迟执行以确保内容渲染完成
                setTimeout(equalizeSlideHeights, 100)
              }}
              onSlideChange={swiper => {
                setSwiperState({
                  isBeginning: swiper.isBeginning,
                  isEnd: swiper.isEnd,
                })
              }}
              className="!overflow-visible"
              breakpoints={{
                1024: {
                  slidesPerView: childrenArray.length <= 3 ? 3 : 3.2,
                },
                1440: {
                  slidesPerView: childrenArray.length <= 4 ? 4 : 4.2,
                },
              }}
            >
              {childrenArray.map((child, index) => (
                <SwiperSlide
                  key={index}
                  className="tablet:!w-[288px] desktop:!w-[316px] lg-desktop:!w-[404px] rounded-box h-fit !w-[296px] overflow-hidden"
                >
                  <div className="w-full max-w-full">{child}</div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* 自定义导航按钮 - hover 时显示，到头时隐藏 */}
            <button
              className={cn(
                `${prevButtonClass} absolute left-4 top-1/2 z-10 -translate-y-1/2`,
                swiperState.isBeginning ? 'hidden' : 'tablet:group-hover:block hidden'
              )}
              aria-label={copy?.previousSlideLabel}
              disabled={swiperState.isBeginning}
            >
              <PrevIcon disabled={swiperState.isBeginning} />
            </button>
            <button
              className={cn(
                `${nextButtonClass} absolute right-4 top-1/2 z-10 -translate-y-1/2`,
                swiperState.isEnd ? 'hidden' : 'tablet:group-hover:block hidden'
              )}
              aria-label={copy?.nextSlideLabel}
              disabled={swiperState.isEnd}
            >
              <NextIcon disabled={swiperState.isEnd} />
            </button>
          </div>
        )}
      </div>
    )
  }
)

CreditsShelfProductsList.displayName = 'CreditsShelf.ProductsList'

export { CreditsShelfProductsList }
